import axios from 'axios';
import { authService } from './authService';
import config from '../config/environment';

// Booking types
export interface ServiceBookingRequest {
  customerId: string;
  serviceId: number;
  slotStart: string;
  slotEnd?: string;
  requestStatus: 'PENDING_PROVIDER' | 'CONFIRMED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  paymentMode: 'PREPAID' | 'POSTPAID';
  paymentStatus: 'PENDING' | 'COMPLETED' | 'FAILED' | 'NOT_REQUIRED';
  priceEstimate: string;
  basePrice?: number; // Base price before GST and other charges
  assignedProviderId?: string;
  addressId: string;
  maxProviders: number;
  planCode: string; // Plan code like BASIC, STANDARD, PREMIUM
  cityCode: string; // City code like DEL, VNS, etc.
  discountCode?: string; // Optional discount code like WELCOME10
  serviceLocationType: 'PLATFORM_SITE' | 'USER_SITE'; // Service location type
}

export interface ServiceBookingResponse {
  id: number;
  serviceRequestUuid: string;
  customerId: string;
  serviceId: number;
  slotStart: string;
  slotEnd?: string;
  requestStatus: string;
  paymentMode: string;
  paymentStatus: string;
  priceEstimate: number;
  assignedProviderId?: string;
  addressId: string;
  groupBookingId?: string;
  createdAt: string;
  updatedAt: string;
}

// Group booking API interfaces
export interface GroupBookingApiResponse {
  message: string;
  totalCount: number;
  groupBookingId: string;
  type: 'CART' | 'SINGLE';
  serviceRequests: ServiceBookingResponse[];
}

export interface UserInterestRequest {
  userUuid: string;
  serviceId: number;
  serviceName: string;
  selectedPlan: string;
  requestDate: string;
}

export interface CreateOrderRequest {
  groupBookingId: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  paymentMode?: 'PREPAID' | 'POSTPAID';
  gstBillingInfo?: {
    billingName: string;
    gstNumber: string;
    billingAddressLine1: string;
    billingAddressLine2: string;
    city: string;
    state: string;
    pincode: string;
    phone: string;
    email: string;
    isDefault: boolean;
  };
}

export interface CreateOrderResponse {
  orderId: string;
  razorpayOrderId: string;
  amount: number;
  currency: string;
  status: string;
  razorpayKeyId: string;
  groupBookingId: string;
  paymentId: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
}

export interface BookingFormData {
  serviceId: number;
  serviceName: string; // Keep for display purposes
  selectedDate: string;
  selectedTime: string;
  duration?: number | string; // Allow both number (minutes) and string (e.g., "2 hours")
  priceEstimate: number;
  basePrice?: number; // Base price before GST (for platform site pricing)
  addressId: string;
  paymentMode: 'PREPAID' | 'POSTPAID';
  selectedPlan?: {
    priests?: number;
    [key: string]: any;
  };
  planCode?: string; // Plan code like BASIC, STANDARD, PREMIUM
  cityCode?: string; // City code like DEL, VNS, etc.
  discountCode?: string; // Optional discount code like WELCOME10
  performAtAntaryatra?: boolean; // Whether service is performed at Antaryatra site
}

// Create axios instance for booking API calls
const bookingApi = axios.create({
  baseURL: config.authServerUrl, // Using same base URL as auth service
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include access token
bookingApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token refresh
bookingApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        await authService.refreshAccessToken();
        const token = localStorage.getItem('access_token');
        if (token) {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return bookingApi(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, redirect to login
        authService.logoutLocal();
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

// Helper function to format datetime for API
const formatDateTimeForApi = (date: string, time: string): string => {
  // Combine date and time and format to UTC ISO string directly
  // This prevents double timezone conversion on the server side
  const dateTime = new Date(`${date}T${time}`);
  return dateTime.toISOString(); // Send in UTC format (ends with 'Z')
};

// Helper function to parse duration string and convert to minutes
const parseDurationToMinutes = (duration: string | number | undefined): number => {
  if (!duration) return 60; // Default to 1 hour
  
  if (typeof duration === 'number') {
    return duration; // Already in minutes
  }
  
  // Parse duration string like "2 hours", "3-4 hours", "1.5 hours"
  const durationStr = duration.toLowerCase().trim();
  
  // Extract first number (in case of ranges like "3-4 hours")
  const hoursMatch = durationStr.match(/(\d+\.?\d*)/);
  if (hoursMatch) {
    const hours = parseFloat(hoursMatch[1]);
    return Math.round(hours * 60); // Convert hours to minutes
  }
  
  // Fallback to 1 hour if parsing fails
  return 60;
};

// Helper function to calculate slot end time
const calculateSlotEnd = (startTime: string, durationMinutes: number = 60): string => {
  const start = new Date(startTime); // startTime is already in UTC format
  const end = new Date(start.getTime() + (durationMinutes * 60 * 1000));
  return end.toISOString(); // Return UTC format
};

export const bookingService = {
  /**
   * Create multiple service bookings (for cart items)
   */
  createGroupBooking: async (bookingRequests: ServiceBookingRequest[]): Promise<GroupBookingApiResponse> => {
    try {
      console.log('📅 Creating group booking:', bookingRequests);
      
      // Get user UUID from JWT token
      const userUuid = authService.getUserUuid();
      
      // Ensure all requests have the same customerId
      const requestsWithUserId = bookingRequests.map(request => ({
        ...request,
        customerId: userUuid
      }));
      
      console.log('📤 Sending group booking request:', requestsWithUserId);
      
      const response = await bookingApi.post('/api/service-requests', requestsWithUserId);
      
      console.log('✅ Group booking created successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error creating group booking:', error);
      
      if (axios.isAxiosError(error)) {
        if (error.response) {
          const errorMessage = error.response.data?.message || error.response.data?.error || 'Failed to create group booking';
          throw new Error(`Failed to create group booking: ${errorMessage}`);
        } else if (error.request) {
          throw new Error('Network error: Could not reach server');
        }
      }
      
      throw new Error(`Failed to create group booking: ${(error as Error).message}`);
    }
  },

  /**
   * Create a single service booking (wrapper for backward compatibility)
   */
  createServiceBooking: async (bookingData: BookingFormData): Promise<GroupBookingApiResponse> => {
    try {
      console.log('📅 Creating single service booking:', bookingData);
      
      // Get user UUID from JWT token
      const userUuid = authService.getUserUuid();
      console.log('🔑 Using user UUID from JWT:', userUuid);
      
      // Format slot start time
      const slotStart = formatDateTimeForApi(bookingData.selectedDate, bookingData.selectedTime);
      
      // Calculate slot end time if duration is provided
      const durationInMinutes = parseDurationToMinutes(bookingData.duration);
      const slotEnd = calculateSlotEnd(slotStart, durationInMinutes);
      
      // Determine payment status based on payment mode
      const paymentStatus = bookingData.paymentMode === 'POSTPAID' ? 'NOT_REQUIRED' : 'PENDING';
      
      // Prepare request payload as array (even for single booking)
      const requestPayload: ServiceBookingRequest[] = [{
        customerId: userUuid,
        serviceId: bookingData.serviceId,
        slotStart,
        slotEnd,
        requestStatus: 'PENDING_PROVIDER',
        paymentMode: bookingData.paymentMode,
        paymentStatus,
        priceEstimate: bookingData.priceEstimate.toString(),
        basePrice: bookingData.basePrice, // Include base price for platform site pricing
        assignedProviderId: '',
        addressId: bookingData.addressId,
        maxProviders: bookingData.selectedPlan?.priests || 1,
        planCode: bookingData.planCode || 'STANDARD',
        cityCode: bookingData.performAtAntaryatra ? 'PLATFORM_SITE' : (bookingData.cityCode || 'DEL'),
        discountCode: bookingData.discountCode,
        serviceLocationType: bookingData.performAtAntaryatra ? 'PLATFORM_SITE' : 'USER_SITE' // Add service location type
      }];
      
      console.log('📤 Sending single booking request:', requestPayload);
      
      const response = await bookingApi.post('/api/service-requests', requestPayload);
      
      console.log('✅ Service booking created successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error creating service booking:', error);
      
      if (axios.isAxiosError(error)) {
        if (error.response) {
          const errorMessage = error.response.data?.message || error.response.data?.error || 'Failed to create booking';
          throw new Error(`Failed to create booking: ${errorMessage}`);
        } else if (error.request) {
          throw new Error('Network error: Could not reach server');
        }
      }
      
      throw new Error(`Failed to create booking: ${(error as Error).message}`);
    }
  },

  /**
   * Create order from group booking
   */
  createOrder: async (orderData: CreateOrderRequest): Promise<CreateOrderResponse> => {
    try {
      console.log('💳 Creating order:', orderData);
      
      const response = await bookingApi.post('/api/orders', orderData);
      
      console.log('✅ Order created successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error creating order:', error);
      
      if (axios.isAxiosError(error)) {
        if (error.response) {
          const errorMessage = error.response.data?.message || error.response.data?.error || 'Failed to create order';
          throw new Error(`Failed to create order: ${errorMessage}`);
        } else if (error.request) {
          throw new Error('Network error: Could not reach server');
        }
      }
      
      throw new Error(`Failed to create order: ${(error as Error).message}`);
    }
  },

  /**
   * Get user's service bookings
   */
  getUserBookings: async (customerId?: string): Promise<ServiceBookingResponse[]> => {
    try {
      // Use provided customerId or get user UUID from JWT token
      const userId = customerId || authService.getUserUuid();
      console.log('🔍 Fetching bookings for user UUID:', userId);
      
      const response = await bookingApi.get(`/api/service-requests/customer/${userId}`);
      
      console.log('✅ Bookings fetched successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching user bookings:', error);
      
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 404) {
          // User has no bookings yet, return empty array
          console.log('ℹ️ No bookings found for user, returning empty array');
          return [];
        }
        
        if (error.response) {
          const errorMessage = error.response.data?.message || error.response.data?.error || 'Failed to fetch bookings';
          throw new Error(`Failed to fetch bookings: ${errorMessage}`);
        } else if (error.request) {
          throw new Error('Network error: Could not reach server');
        }
      }
      
      throw new Error(`Failed to fetch bookings: ${(error as Error).message}`);
    }
  },

  /**
   * Get booking by ID
   */
  getBookingById: async (bookingId: string): Promise<ServiceBookingResponse> => {
    try {
      console.log('🔍 Fetching booking by ID:', bookingId);
      
      const response = await bookingApi.get(`/api/service-requests/${bookingId}`);
      
      console.log('✅ Booking fetched successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching booking by ID:', error);
      
      if (axios.isAxiosError(error)) {
        if (error.response) {
          const errorMessage = error.response.data?.message || error.response.data?.error || 'Failed to fetch booking';
          throw new Error(`Failed to fetch booking: ${errorMessage}`);
        } else if (error.request) {
          throw new Error('Network error: Could not reach server');
        }
      }
      
      throw new Error(`Failed to fetch booking: ${(error as Error).message}`);
    }
  },

  /**
   * Update booking status
   */
  updateBookingStatus: async (bookingId: string, status: ServiceBookingRequest['requestStatus']): Promise<ServiceBookingResponse> => {
    try {
      console.log('🔄 Updating booking status:', bookingId, status);
      
      const response = await bookingApi.patch(`/api/service-requests/${bookingId}/status`, {
        requestStatus: status
      });
      
      console.log('✅ Booking status updated successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error updating booking status:', error);
      
      if (axios.isAxiosError(error)) {
        if (error.response) {
          const errorMessage = error.response.data?.message || error.response.data?.error || 'Failed to update booking status';
          throw new Error(`Failed to update booking status: ${errorMessage}`);
        } else if (error.request) {
          throw new Error('Network error: Could not reach server');
        }
      }
      
      throw new Error(`Failed to update booking status: ${(error as Error).message}`);
    }
  },

  /**
   * Update payment status
   */
  updatePaymentStatus: async (bookingId: string, paymentStatus: ServiceBookingRequest['paymentStatus']): Promise<ServiceBookingResponse> => {
    try {
      console.log('💳 Updating payment status:', bookingId, paymentStatus);
      
      const response = await bookingApi.patch(`/api/service-requests/${bookingId}/payment`, {
        paymentStatus
      });
      
      console.log('✅ Payment status updated successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error updating payment status:', error);
      
      if (axios.isAxiosError(error)) {
        if (error.response) {
          const errorMessage = error.response.data?.message || error.response.data?.error || 'Failed to update payment status';
          throw new Error(`Failed to update payment status: ${errorMessage}`);
        } else if (error.request) {
          throw new Error('Network error: Could not reach server');
        }
      }
      
      throw new Error(`Failed to update payment status: ${(error as Error).message}`);
    }
  },

  /**
   * Cancel a booking
   */
  cancelBooking: async (bookingId: string): Promise<ServiceBookingResponse> => {
    try {
      console.log('❌ Cancelling booking:', bookingId);
      
      const response = await bookingApi.patch(`/api/service-requests/${bookingId}/cancel`);
      
      console.log('✅ Booking cancelled successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error cancelling booking:', error);
      
      if (axios.isAxiosError(error)) {
        if (error.response) {
          const errorMessage = error.response.data?.message || error.response.data?.error || 'Failed to cancel booking';
          throw new Error(`Failed to cancel booking: ${errorMessage}`);
        } else if (error.request) {
          throw new Error('Network error: Could not reach server');
        }
      }
      
      throw new Error(`Failed to cancel booking: ${(error as Error).message}`);
    }
  },

  /**
   * Process payment for prepaid bookings
   */
  processPayment: async (bookingId: string, paymentDetails: any): Promise<ServiceBookingResponse> => {
    try {
      console.log('💳 Processing payment for booking:', bookingId);
      
      const response = await bookingApi.post(`/api/service-requests/${bookingId}/payment`, paymentDetails);
      
      console.log('✅ Payment processed successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error processing payment:', error);
      
      if (axios.isAxiosError(error)) {
        if (error.response) {
          const errorMessage = error.response.data?.message || error.response.data?.error || 'Failed to process payment';
          throw new Error(`Failed to process payment: ${errorMessage}`);
        } else if (error.request) {
          throw new Error('Network error: Could not reach server');
        }
      }
      
      throw new Error(`Failed to process payment: ${(error as Error).message}`);
    }
  },

  /**
   * Get payment status for a booking
   */
  getPaymentStatus: async (serviceRequestUuid: string): Promise<{ paymentStatus: string; paymentMode: string }> => {
    try {
      console.log('🔍 Getting payment status for:', serviceRequestUuid);
      
      const response = await bookingApi.get(`/api/service-requests/${serviceRequestUuid}/payment-status`);
      
      console.log('✅ Payment status retrieved:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error getting payment status:', error);
      
      if (axios.isAxiosError(error)) {
        if (error.response) {
          const errorMessage = error.response.data?.message || error.response.data?.error || 'Failed to get payment status';
          throw new Error(`Failed to get payment status: ${errorMessage}`);
        } else if (error.request) {
          throw new Error('Network error: Could not reach server');
        }
      }
      
      throw new Error(`Failed to get payment status: ${(error as Error).message}`);
    }
  },

  /**
   * Capture user interest for high-demand services
   */
  captureUserInterest: async (userInterest: UserInterestRequest): Promise<{ message: string }> => {
    try {
      console.log('📝 Capturing user interest:', userInterest);
      
      const response = await bookingApi.post('/api/user-interest/capture', userInterest);
      
      console.log('✅ User interest captured successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error capturing user interest:', error);
      
      if (axios.isAxiosError(error)) {
        if (error.response) {
          const errorMessage = error.response.data?.message || error.response.data?.error || 'Failed to capture user interest';
          throw new Error(`Failed to capture user interest: ${errorMessage}`);
        } else if (error.request) {
          throw new Error('Network error: Could not reach server');
        }
      }
      
      throw new Error(`Failed to capture user interest: ${(error as Error).message}`);
    }
  }
};

export default bookingService;